'use client'

import { clsx } from 'clsx'
import { type ComponentPropsWithoutRef, type ElementType, type FC } from 'react'

import {
  type GkAirRecommendedProductAttributesWeb,
  type GkAirRecommendedProductDataWeb,
} from '@redteclab/api/clients/crosssellers-proxy-service'
import { type Metadata } from '@redteclab/api/clients/retail-media-xsell-service'

import { AD_SERVER_XSELL_PRODUCT_ID_DATA_ATTR } from '../../../ad-server'
import { EXPERIMENTS_TRACK_EVENT_RECOMMENDATION_PRODUCT_CLICK } from '../../../experiments/eventTracking'
import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../experiments/hooks'
import { recommendationAttributionFromRecommendedProduct } from '../../../experiments/recommendation-attribution'
import { recommendationExperimentsTrackRecommendationEvent } from '../../recommendation-experiments'
import { useRecommendationWidgetContext } from '../RecommendationAreaProvider'

import { RecommendationProductImage } from './RecommendationProductImage'
import { RecommendationProductName } from './RecommendationProductName'
import { RecommendationProductPrice } from './RecommendationProductPrice'
import { RecommendationProductQuantity } from './RecommendationProductQuantity'
import { RecommendationProductRating } from './RecommendationProductRating'

type RecommendationHorizontalSliderItemProps =
  ComponentPropsWithoutRef<'section'> & {
    onProductLinkClick?: () => void
    product?: GkAirRecommendedProductDataWeb
    slots?: {
      ctaButton?: ElementType
      image?: ElementType
      shippingPrice?: ElementType
    }
    trackingToken: GkAirRecommendedProductAttributesWeb['trackingtoken']
  }

export const RecommendationProductCard: FC<
  RecommendationHorizontalSliderItemProps
> = ({
  className,
  onProductLinkClick,
  product,
  slots,
  trackingToken,
  ...rest
}) => {
  const ImageComponent = slots?.image ?? RecommendationProductImage
  const CtaComponent = slots?.ctaButton
  const ShippingPriceComponent = slots?.shippingPrice
  const { adServerMetadata, serviceId, widgetId } =
    useRecommendationWidgetContext()
  const metadataContent = product?.UID
    ? adServerMetadata?.[product.UID as keyof Metadata]
    : undefined
  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)

  const handleProductLinkClick = (): void => {
    trackClick()

    if (product) {
      recommendationExperimentsTrackRecommendationEvent({
        attribution: recommendationAttributionFromRecommendedProduct(product),
        eventKey: EXPERIMENTS_TRACK_EVENT_RECOMMENDATION_PRODUCT_CLICK,
        offerId: product.offer_id,
        serviceId,
        widgetId,
      })
    }

    onProductLinkClick?.()
  }

  const adServerProps = {
    /*
     * Re-ranked / injected elements from ad server needs to be marked here
     * They're being grabbed in ProductAvailableRecommendationArea.tsx
     * and pushed into ad server data layer
     */
    [AD_SERVER_XSELL_PRODUCT_ID_DATA_ATTR]:
      metadataContent && product?.UID ? product.UID : undefined,
  }

  return (
    <section
      className={clsx(
        'relative mb-2 flex flex-1 flex-col overflow-hidden rounded-m bg-light-primary-low shadow-m',
        className,
      )}
      data-product-id={product?.UID}
      {...adServerProps}
      {...rest}
    >
      <ImageComponent product={product} />
      <div className="flex flex-col gap-1 px-2 py-2.5">
        <RecommendationProductName
          onClick={handleProductLinkClick}
          product={product}
        />
        <div className="flex-1 justify-between">
          <RecommendationProductRating product={product} />
          <RecommendationProductQuantity product={product} />
          <RecommendationProductPrice product={product} />
        </div>
      </div>
      {CtaComponent ? (
        <div className="relative px-2 pb-2.5">
          <CtaComponent
            metadataContent={metadataContent}
            product={product}
            trackingToken={trackingToken}
          />
        </div>
      ) : null}
      {ShippingPriceComponent ? (
        <ShippingPriceComponent product={product} />
      ) : null}
    </section>
  )
}
